import { useMemo } from 'react';

import { calculateIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import type { WatchlistItem } from '@/hooks/useWatchlist';
import { useWatchlistSorting } from './useWatchlistSorting';
import { WatchlistSorting } from './types';

type SortingFunction<T> = (a: T, b: T) => -1 | 0 | 1;

export const sortValue = <Value extends string | number>(
  a: Value | null,
  b: Value | null,
  direction: 'asc' | 'desc'
) => {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  if (direction === 'desc') {
    return a < b ? 1 : -1;
  }
  return a < b ? -1 : 1;
};

const sortingFns: Record<WatchlistSorting, SortingFunction<WatchlistItem>> = {
  [WatchlistSorting.RECENTLY_ADDED]: (
    { createdAt: a }: WatchlistItem,
    { createdAt: b }: WatchlistItem
  ) => sortValue(a, b, 'desc'),
  [WatchlistSorting.MARKET_CAP_HIGH_TO_LOW]: (
    a: WatchlistItem,
    b: WatchlistItem
  ) =>
    sortValue(
      a.company.analysisValue?.marketCapUSD ?? null,
      b.company.analysisValue?.marketCapUSD ?? null,
      'desc'
    ),
  [WatchlistSorting.COMPANY_NAME_A_TO_Z]: (
    { company: { name: a } }: WatchlistItem,
    { company: { name: b } }: WatchlistItem
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  [WatchlistSorting.TICKER_A_TO_Z]: (
    { company: { tickerSymbol: a } }: WatchlistItem,
    { company: { tickerSymbol: b } }: WatchlistItem
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  [WatchlistSorting.PERFORMANCE_7D]: (a: WatchlistItem, b: WatchlistItem) =>
    sortValue(
      a.company.analysisValue?.return7d ?? null,
      b.company.analysisValue?.return7d ?? null,
      'desc'
    ),
  [WatchlistSorting.DISCOUNT_HIGH_TO_LOW]: (
    a: WatchlistItem,
    b: WatchlistItem
  ) =>
    sortValue(
      calculateIntrinsicDiscount(
        a.company.analysisValue?.lastSharePrice ?? null,
        a.company.analysisValue?.npvPerShare ?? null,
        a.company.myValuation?.narrative?.draftUpdate?.valuation.fairValue ??
          a.company.myValuation?.fairValue ??
          a.company.defaultNarrative?.valuation.fairValue
      ),
      calculateIntrinsicDiscount(
        b.company.analysisValue?.lastSharePrice ?? null,
        b.company.analysisValue?.npvPerShare ?? null,
        b.company.myValuation?.narrative?.draftUpdate?.valuation.fairValue ??
          b.company.myValuation?.fairValue ??
          b.company.defaultNarrative?.valuation.fairValue
      ),
      'desc'
    ),
  [WatchlistSorting.DISCOUNT_LOW_TO_HIGH]: (
    a: WatchlistItem,
    b: WatchlistItem
  ) =>
    sortValue(
      calculateIntrinsicDiscount(
        a.company.analysisValue?.lastSharePrice ?? null,
        a.company.analysisValue?.npvPerShare ?? null,
        a.company.myValuation?.narrative?.draftUpdate?.valuation.fairValue ??
          a.company.myValuation?.fairValue ??
          a.company.defaultNarrative?.valuation.fairValue
      ),
      calculateIntrinsicDiscount(
        b.company.analysisValue?.lastSharePrice ?? null,
        b.company.analysisValue?.npvPerShare ?? null,
        b.company.myValuation?.narrative?.draftUpdate?.valuation.fairValue ??
          b.company.myValuation?.fairValue ??
          b.company.defaultNarrative?.valuation.fairValue
      ),
      'asc'
    ),
  [WatchlistSorting.TOTAL_SCORE_HIGH_TO_LOW]: (
    { company: { score: a } }: WatchlistItem,
    { company: { score: b } }: WatchlistItem
  ) => sortValue(a?.total || 0, b?.total || 0, 'desc'),
  [WatchlistSorting.VALUE_SCORE_HIGH_TO_LOW]: (
    { company: { score: a } }: WatchlistItem,
    { company: { score: b } }: WatchlistItem
  ) => sortValue(a?.value || 0, b?.value || 0, 'desc'),
  [WatchlistSorting.FUTURE_SCORE_HIGH_TO_LOW]: (
    { company: { score: a } }: WatchlistItem,
    { company: { score: b } }: WatchlistItem
  ) => sortValue(a?.future || 0, b?.future || 0, 'desc'),
  [WatchlistSorting.PAST_SCORE_HIGH_TO_LOW]: (
    { company: { score: a } }: WatchlistItem,
    { company: { score: b } }: WatchlistItem
  ) => sortValue(a?.past || 0, b?.past || 0, 'desc'),
  [WatchlistSorting.DIVIDEND_SCORE_HIGH_TO_LOW]: (
    { company: { score: a } }: WatchlistItem,
    { company: { score: b } }: WatchlistItem
  ) => sortValue(a?.dividend || 0, b?.dividend || 0, 'desc'),
  [WatchlistSorting.HEALTH_SCORE_HIGH_TO_LOW]: (
    { company: { score: a } }: WatchlistItem,
    { company: { score: b } }: WatchlistItem
  ) => sortValue(a?.health || 0, b?.health || 0, 'desc'),
};

export const useSortedWatchlistItems = (items: WatchlistItem[] = []) => {
  const [sorting] = useWatchlistSorting();

  return useMemo(() => {
    const sortingFn = sortingFns[sorting];

    if (typeof sortingFn === 'undefined') {
      return items;
    }

    return [...items].sort(sortingFn);
  }, [items, sorting]);
};
